// voloService.ts - Service for Volo database operations
import { getAuth } from "firebase/auth";
import {
  Account,
  AccountDetailsResponse,
  AccountsResponse,
  ApiError,
  DataResponse,
  Portfolio,
  PortfolioPayload,
  Property,
  PropertyPayload,
  PropertiesResponse,
  UsersResponse,
  UserPayload,
  User,
  // FinancialAccount,
  FinancialAccountsResponse,
  ProformaLineItemsResponse,
  SaveFinancialAccountMappingsResponse,
  FinancialAccountMapping,
} from "../../global-types";

const API_BASE_URL = import.meta.env.DEV
  ? "http://127.0.0.1:5002/xanathar-fa480/us-central1/api/volo"
  : "https://us-central1-xanathar-fa480.cloudfunctions.net/api/volo";

const getIdToken = async (): Promise<string | null> => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    console.warn("No authenticated user found");
    return null;
  }

  try {
    return await user.getIdToken();
  } catch (error) {
    console.error("Error getting ID token:", error);
    return null;
  }
};

// Helper function for handling API responses
const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}) as ApiError);
    throw new Error(error.error || "An error occurred");
  }
  return response.json() as Promise<T>;
};

// Helper function for making authenticated requests
const fetchWithAuth = async <T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> => {
  const token = await getIdToken();
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(endpoint, {
    ...options,
    headers,
  });

  return handleResponse<T>(response);
};

export const fetchAccounts = async (): Promise<AccountsResponse> => {
  try {
    return await fetchWithAuth<AccountsResponse>(`${API_BASE_URL}/accounts`);
  } catch (error) {
    console.error("Error fetching Volo accounts:", error);
    throw error;
  }
};

export const fetchAllProperties = async (): Promise<PropertiesResponse> => {
  try {
    return await fetchWithAuth<PropertiesResponse>(`${API_BASE_URL}/properties`);
  } catch (error) {
    console.error("Error fetching all properties:", error);
    throw error;
  }
};

// Fetch table data from Volo database
export const fetchData = async (): Promise<DataResponse> => {
  try {
    return await fetchWithAuth<DataResponse>(`${API_BASE_URL}/data`);
  } catch (error) {
    console.error("Error fetching Volo data:", error);
    throw error;
  }
};

export const fetchUsers = async (): Promise<UsersResponse> => {
  try {
    return await fetchWithAuth<UsersResponse>(`${API_BASE_URL}/users`);
  } catch (error) {
    console.error("Error fetching Volo users:", error);
    throw error;
  }
};

export const fetchAccountDetails = async (
  accountId: string,
): Promise<AccountDetailsResponse> => {
  try {
    return await fetchWithAuth<AccountDetailsResponse>(
      `${API_BASE_URL}/accounts/${accountId}`,
    );
  } catch (error) {
    console.error("Error fetching Volo account details:", error);
    throw error;
  }
};

export const createAccount = async (accountData: {
  name: string;
  tableauSecret?: string;
  tableauSecretId?: string;
  tableauClientId?: string;
  tableauUserId?: string;
}): Promise<{ account: Account; message: string }> => {
  try {
    return await fetchWithAuth<{ account: Account; message: string }>(
      `${API_BASE_URL}/accounts`,
      {
        method: "POST",
        body: JSON.stringify(accountData),
      },
    );
  } catch (error) {
    console.error("Error creating Volo account:", error);
    throw error;
  }
};

export const upsertPortfolio = async (
  portfolioData: PortfolioPayload,
): Promise<{ portfolio: Portfolio; message: string }> => {
  try {
    return await fetchWithAuth<{ portfolio: Portfolio; message: string }>(
      `${API_BASE_URL}/portfolios`,
      {
        method: "POST",
        body: JSON.stringify(portfolioData),
      },
    );
  } catch (error) {
    console.error("Error creating Volo portfolio: ", error);
    throw error;
  }
};

export const upsertProperty = async (
  propertyData: PropertyPayload,
): Promise<{ property: Property; message: string }> => {
  try {
    /**@todo make a better URL endpoint */
    return await fetchWithAuth<{ property: Property; message: string }>(
      `${API_BASE_URL}/properties`,
      {
        method: "POST",
        body: JSON.stringify(propertyData),
      },
    );
  } catch (error) {
    console.error("Error updating property:", error);
    throw error;
  }
};

export const upsertUser = async (
  userData: UserPayload,
): Promise<{ user: User; message: string }> => {
  try {
    return await fetchWithAuth<{ user: User; message: string }>(
      `${API_BASE_URL}/users`,
      {
        method: "POST",
        body: JSON.stringify(userData),
      },
    );
  } catch (error) {
    console.error("Error updating user:", error);
    throw error;
  }
};

export const fetchFinancialAccounts = async (
  accountId: string,
): Promise<FinancialAccountsResponse> => {
  try {
    return await fetchWithAuth<FinancialAccountsResponse>(
      `${API_BASE_URL}/accounts/${accountId}/financial-accounts`,
    );
  } catch (error) {
    console.error("Error fetching Volo financial accounts:", error);
    throw error;
  }
};

export const fetchProformaLineItems = async (
  accountId: string,
): Promise<ProformaLineItemsResponse> => {
  try {
    return await fetchWithAuth<ProformaLineItemsResponse>(
      `${API_BASE_URL}/accounts/${accountId}/proforma-line-items`,
    );
  } catch (error) {
    console.error("Error fetching Volo proforma line items:", error);
    throw error;
  }
};

export const saveFinancialAccountMappings = async (
  accountId: string,
  mappings: FinancialAccountMapping[],
): Promise<SaveFinancialAccountMappingsResponse> => {
  try {
    return await fetchWithAuth<SaveFinancialAccountMappingsResponse>(
      `${API_BASE_URL}/accounts/${accountId}/financial-accounts/mappings`,
      {
        method: "POST",
        body: JSON.stringify({ mappings }), // always wrap in { mappings }
      },
    );
  } catch (error) {
    console.error("Error saving Volo financial account mappings:", error);
    throw error;
  }
};
